/**
 * Locale-aware URL helpers.
 *
 * Every internal link that leaves the server (nav items, CTA buttons, rich-text
 * hyperlinks, card slugs, etc.) should pass through `localizeUrl` so the href
 * always carries the locale the visitor is currently browsing.
 *
 * The regex mirrors the pattern used in `proxy.ts` for detecting locale
 * segments, extended to cover script subtags like `zh-Hant`.
 */

import { DEFAULT_PUBLIC_LANG } from "@/lib/locale/toContentfulLocale"

/** Matches the first path segment when it looks like a locale code.
 *  Covers:  /en/  /en-US/  /zh-Hant/  /zh-Hant-tw/  /fr  (at end of string) */
const LOCALE_PREFIX_RE = /^\/[a-z]{2}(?:-[A-Za-z]{4})?(?:-[A-Za-z]{2})?(?:\/|$)/

const LOCALE_SEGMENT_RE =
  /^\/([a-z]{2}(?:-[A-Za-z]{4})?(?:-[A-Za-z]{2})?)(?:\/|$)/i

/**
 * Extract the public locale code from a pathname (e.g. `/de/about` → `de`).
 * Falls back to `DEFAULT_PUBLIC_LANG` when no locale segment is present.
 */
export function getLangFromPathname(pathname: string): string {
  const match = pathname.match(LOCALE_SEGMENT_RE)
  return match?.[1] ?? DEFAULT_PUBLIC_LANG
}


/** Matches non-http URI schemes (mailto:, tel:, sms:) that must never be
 *  treated as internal paths or locale-prefixed. */
const PROTOCOL_LINK_RE = /^(mailto|tel|sms):/i

export function isExternalUrl(url: string): boolean {
  if (!url) return false
  return (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("//") ||
    PROTOCOL_LINK_RE.test(url)
  )
}

/**
 * Remove a leading locale segment from `path`, if present.
 *
 * Examples:
 *   stripLocalePrefix('/en-US/about')  → '/about'
 *   stripLocalePrefix('/fr/products')  → '/products'
 *   stripLocalePrefix('/zh-Hant/')     → '/'
 *   stripLocalePrefix('/about')        → '/about'   (no locale found)
 */
export function stripLocalePrefix(path: string): string {
  if (!path) return path
  const match = path.match(LOCALE_PREFIX_RE)
  if (!match) return path
  const stripped = path.slice(match[0].length - (match[2] === "/" ? 1 : 0))
  return "/" + stripped || "/"
}

function isLocaleOnlyPath(path: string): boolean {
  const match = path.match(LOCALE_PREFIX_RE)
  if (!match) return false
  const remainder = path.slice(
    match[0].length - (match[0].endsWith("/") ? 1 : 0),
  )
  return remainder === "" || remainder === "/"
}

/**
 * Return `url` with the correct `lang` prefix.
 *
 * - External URLs → returned unchanged.
 * - Anchor-only links (#section) → returned unchanged.
 * - Internal paths → any existing locale prefix is stripped, then `/${lang}`
 *   is prepended.
 */
export function localizeUrl(url: string, lang: string): string {
  if (!url || !lang) return url || ""
  if (isExternalUrl(url)) return url
  if (url.startsWith("#")) return url
  if (url.startsWith("/#")) return url.slice(1)

  const hashIndex = url.indexOf("#")
  const path = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex)

  if (!path.startsWith("/")) return url

  if (hash && (path === "/" || isLocaleOnlyPath(path))) {
    return hash
  }

  const stripped = stripLocalePrefix(path)
  return `/${lang}${stripped}${hash}`
}
